import PageLayout from "components/layout/PageLayout"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
    useGetPrivacyPolicyTemplateQuery,
    useCreateOrUpdatePrivacyPolicyMutation,
    useGetPrivacyPolicyDetailsQuery
} from "./api/privacyPolicyApiSlice"
import AvatarMini from "components/commons/AvatarMini"

export default function PrivacyPolicyEditor() {
    const { t } = useTranslation("global")

    // API
    const {
        data: privacyPolicyTemplate,
        isFetching: isFetchingTemplate,
        isLoading: isLoadingTemplate,
        isSuccess: isSuccessTemplate,
        isError: isErrorTemplate,
        error: errorTemplate,
        refetch: refetchTemplate,
    } = useGetPrivacyPolicyTemplateQuery()

    const {
        data: privacyPolicyDetails,
        isFetching: isFetchingDetails,
        isLoading: isLoadingDetails,
        isSuccess: isSuccessDetails,
        isError: isErrorDetails,
        error: errorDetails,
        refetch: refetchDetails,
    } = useGetPrivacyPolicyDetailsQuery()

    const [createOrUpdatePrivacyPolicy, { isLoading: isSaving }] = useCreateOrUpdatePrivacyPolicyMutation()

    // Stato locale per l'editor di testo
    const [templateText, setTemplateText] = useState("")
    const [placeholderValues, setPlaceholderValues] = useState({})

    // Carica il contenuto iniziale quando i dati sono pronti
    if (isSuccessTemplate && templateText === "" && Object.keys(placeholderValues).length === 0) {
        setTemplateText(privacyPolicyTemplate.text)
        setPlaceholderValues(privacyPolicyTemplate.placeholders)
    }

    // Gestore per modificare il testo nel textarea
    const handleTextChange = (e) => {
        setTemplateText(e.target.value)
    }

    // Gestore per modificare i valori dei segnaposto
    const handlePlaceholderChange = (key, value) => {
        setPlaceholderValues((prev) => ({ ...prev, [key]: value }))
    }

    // Gestore per salvare le modifiche
    const handleSave = async () => {
        try {
            const payload = {
                text: templateText,
                placeholders: placeholderValues,
            }
            await createOrUpdatePrivacyPolicy(payload).unwrap()
            refetchTemplate()
        } catch (error) {
            console.error("Error saving privacy policy:", error)
        }
    }

    let editorContent = (
        <div className="container privacy-policy-editor">
            {/* TextArea per il template */}
            <div className="mb-4">
                <label htmlFor="templateText" className="form-label">
                    Policy Template
                </label>
                <textarea
                    id="templateText"
                    value={templateText}
                    onChange={handleTextChange}
                    placeholder={t("privacyPolicy.templateEditor.placeholder")}
                    rows={10}
                    className="form-control"
                />
            </div>

            {/* Lista di segnaposto */}
            <div className="mb-4">
                <h3>Placeholders</h3>
                {Object.keys(placeholderValues).length > 0 ? (
                    <ul className="list-group">
                        {Object.keys(placeholderValues).map((key) => (
                            <li key={key} className="list-group-item d-flex align-items-center justify-content-between">
                                <div>
                                    <code>{`{${key}}`}</code>
                                </div>
                                <div className="input-group">
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={placeholderValues[key] || ""}
                                        onChange={(e) => handlePlaceholderChange(key, e.target.value)}
                                        placeholder={t("privacyPolicy.placeholders.inputPlaceholder")}
                                    />
                                    <button
                                        onClick={() => {
                                            setTemplateText((prev) => `${prev}{${key}}`)
                                        }}
                                        className="btn btn-outline-secondary"
                                    >
                                        {t("action.insert")}
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p>{t("privacyPolicy.placeholders.none")}</p>
                )}
            </div>

            <button
                onClick={handleSave}
                className="btn btn-primary"
                disabled={isSaving}
            >
                <i className="fa-solid fa-floppy-disk"></i> {t("action.save")}
            </button>
        </div>
    )

    let previewContent = isSuccessDetails && privacyPolicyDetails && (
        <div className="container privacy-policy-preview">
            <h3>{t("menu.privacyPolicyPreview")}</h3>
            <div className="mb-3 text-end">
                <strong>{t("property.updated_at")}</strong> {new Date(privacyPolicyDetails.updatedAt).toLocaleString()}
            </div>
            <div className="mb-4 p-3 bg-dark rounded">
                <span style={{whiteSpace: "pre-line"}}>{privacyPolicyDetails.text}</span>
            </div>
            <div className="mb-3">
                <h4><i className="fa-solid fa-users"></i> {t("menu.privacyPolicyAcceptances")}</h4>
                {privacyPolicyDetails.acceptances.length > 0 ? (
                    <ul className="list-group ">
                        {privacyPolicyDetails.acceptances.map((acceptance) => (
                            <div className="col" key={acceptance.user.guid}>
                                <AvatarMini  displayName={acceptance.user.displayName} guid={acceptance.user.guid} >
                                    - {t("property.accepted_at")}  <strong>{new Date(acceptance.acceptedAt).toLocaleString()}</strong>
                                </AvatarMini>
                            </div>


                        ))}
                    </ul>
                ) : (
                    <p>{t("helper.no_acceptances_yet")}</p>
                )}
            </div>
        </div>
    )

    return (
        <PageLayout
            pageTitle={t("menu.privacyPolicy")}
            bodyContent={
                <>
                    {editorContent}
                    <hr />
                    {previewContent}
                </>
            }
            onPageRefresh={() => {
                refetchTemplate()
                refetchDetails()
            }}
            isError={isErrorTemplate || isErrorDetails}
            errorMsg={errorTemplate?.message || errorDetails?.message || t("errors.generic")}
            isLoadingPage={isLoadingTemplate || isLoadingDetails}
            isLoadingContent={isFetchingTemplate || isFetchingDetails || isSaving}
        />
    )
}
